import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import {
  ConversationProvider,
  createSocketIoAdapter,
  createChatClient,
  useConversation,
} from "@lobees/chat-core";

import { getData, postData, putData, deleteData, uploadMultipleFile } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import ConversationLeftPanel from "./ConversationLeftPanel";
import ConversationRightPanel from "./ConversationRightPanel";

// Built once at module scope, same as lobees-web's ConversationBody — the
// socket must not be recreated on every render (that was the historical bug
// in web's original implementation, now fixed at the source in
// @lobees/chat-core's ConversationProvider, which only ever calls
// adapter.connect() from a mount-only effect).
const chatAdapter = createSocketIoAdapter({ url: url.SERVER_URL });

const chatClient = createChatClient({
  request: {
    get: getData,
    post: postData,
    put: putData,
    del: deleteData,
    uploadFiles: uploadMultipleFile,
  },
  endpoints: {
    sendChat: url.API_BASE_URL + url.API_SEND_CHAT,
    chatList: url.API_BASE_URL + url.API_GET_ALL_CHAT_LIST,
    chatMessages: url.API_BASE_URL + url.API_GET_ALL_CHAT_MESSAGES,
    updateChat: url.API_BASE_URL + url.API_UPDATE_CHAT,
    deleteChat: url.API_BASE_URL + url.API_DELETE_CHAT,
    reactChat: url.API_BASE_URL + url.API_REACT_CHAT,
    starChat: url.API_BASE_URL + url.API_STAR_CHAT,
    removeRequireResponse: url.API_BASE_URL + url.API_REMOVE_REQUIRE_RESPONSE,
    updateTrackers: url.API_BASE_URL + url.API_UPDATE_TRACKERS,
    translateChat: url.API_BASE_URL + url.API_TRANSLATED_CHAT,
    groupListForChat: url.API_BASE_URL + url.API_GET_GROUP_LIST_FOR_CHAT,
    taskListForChat: url.API_BASE_URL + url.API_GET_TASK_LIST_FOR_CHAT,
    fileUpload: url.API_BASE_URL + url.API_MULTIPLE_FILE_UPLOAD,
  },
  socketUrl: url.SERVER_URL,
  // admin's Gateway.js returns the server's error payload object (or null)
  // on failure, unlike web's bare string — this is the one seam that differs
  // per app, handled entirely here rather than inside the shared package.
  normalizeError: (raw) => raw?.message || "Request failed",
});

// Needs to live INSIDE ConversationProvider (useConversation() requires the
// context), so the mobile single-pane toggle is a separate component rather
// than logic inline in ConversationBody.
const ConversationLayout = ({ topOffset, allContacts, setAllContacts }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { conversation } = useConversation();

  // Mobile: show exactly one pane at a time (contact list, or the open
  // chat) — a fixed 320px sidebar next to a chat pane doesn't work on a
  // phone-sized screen. Desktop/tablet: always show both side by side.
  const showLeftPanel = !isMobile || !conversation;
  const showRightPanel = !isMobile || !!conversation;

  return (
    <Box
      sx={{
        position: "fixed",
        top: topOffset,
        left: 0,
        right: 0,
        bottom: 0,
        // A global rule in public/assets/css/developer.css
        // (`.MuiBox-root { width: max-content }`) overrides every MUI Box's
        // default width app-wide. For a position:fixed element with
        // left/right:0, that global override replaces the implicit
        // "stretch between the offsets" width with a shrink-to-fit one —
        // explicit width here beats it back. (height is intentionally left
        // alone: adding an explicit height here as well over-constrains a
        // fixed box that already has both `top` and `bottom` set, and
        // resolves against the full viewport instead of the space below the
        // header, pushing the composer off-screen.)
        width: "100%",
        display: "flex",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {showLeftPanel && (
        <ConversationLeftPanel
          allContacts={allContacts}
          setAllContacts={setAllContacts}
          fullWidth={isMobile}
        />
      )}
      {showRightPanel && <ConversationRightPanel isMobile={isMobile} />}
    </Box>
  );
};

// First-pass MVP: contact chat only (group/task/ai screens show a "coming
// soon" placeholder in ConversationRightPanel). This proves the shared
// package works end-to-end in lobees-admin before building out full parity
// with lobees-web's feature set.
const ConversationBody = () => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

  const [allContacts, setAllContacts] = useState([]);

  // The admin shell's #header isn't a fixed-height element we can rely on a
  // hardcoded pixel value for (Bootstrap navbar padding varies with content/
  // breakpoint, and can wrap to two lines on small screens), so measure the
  // real height and keep it in sync on resize.
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const headerEl = document.getElementById("header");
    if (!headerEl) return undefined;

    const updateOffset = () => setTopOffset(headerEl.getBoundingClientRect().height);
    updateOffset();

    const observer = new ResizeObserver(updateOffset);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  const currentUser = useMemo(
    () => ({
      id: userInfo?._id,
      email: userInfo?.email,
      name: userInfo?.name,
      surname: userInfo?.surname,
      photoPath: userInfo?.photoimage?.path ?? "",
    }),
    [userInfo?._id]
  );

  return (
    <ConversationProvider adapter={chatAdapter} chatClient={chatClient} currentUser={currentUser}>
      <ConversationLayout topOffset={topOffset} allContacts={allContacts} setAllContacts={setAllContacts} />
    </ConversationProvider>
  );
};

export default ConversationBody;
